import React, { ChangeEvent, useEffect, useState, useContext } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { addRequest, getDataFromtemplateName, updateRequest } from "../../redux/slices/EmailTemplateSlice";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import { Button, TextInput } from "../../components";
import RichTextEditor from "./RichTextEditor";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

interface TemplateData {
  templateName: string;
  templateContent: string;
  updateContent?: string;
}

interface UploadResponse {
  status: string;
  message: string;
  statusMessage?: string;
}

const AddEditTemplate = ({ isAdminPage }: { isAdminPage: boolean }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const [searchParams] = useSearchParams();
  const tempPath = searchParams.get("templateName") || "";

  const [formData, setFormData] = useState<TemplateData>({
    templateName: "",
    templateContent: "",
  });

  const [errorInitiated, setErrorInitiated] = useState(false);

  // Fetch existing template data
  useEffect(() => {
    if (tempPath) {
      (async () => {
        const res = await dispatch(getDataFromtemplateName({ templateName: tempPath }));
        if (res.payload) {
          const responseData = res.payload as { data: TemplateData }; // Explicitly type it
          setFormData(responseData.data);
        } else {
          toast?.openToast("Something went wrong");
        }
      })();
    }
  }, [tempPath, dispatch, toast]);

  // Handle Input and Editor Changes
  const handleChange = (e: ChangeEvent<HTMLInputElement> | string) => {
    if (typeof e === "string") {
      setFormData(prev => ({ ...prev, templateContent: e }));
    } else {
      const { name, value } = e.target;
      if (name === "templateName" && value.length > 50) return;
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Reusable function for Add/Update
  const handleTemplateAction = async (action: "add" | "update") => {
    setErrorInitiated(true);

    if (!formData.templateName.trim() || !formData.templateContent.trim()) return;

    const actionType = action === "add" ? addRequest : updateRequest;
    let requestData: any = { ...formData };

    if (action === "update") {
      requestData = {
        ...formData,
        updateContent: formData.templateContent,
      };
      delete requestData.templateContent; // This is allowed now because requestData is of type `any`
    }
    const res = await dispatch(actionType({ request: requestData }));

    const data = res.payload as UploadResponse;
    toast?.openToast(data.statusMessage);
    navigate("/template-listing");
  };

  return (
    <main>
      <div className="container mx-auto p-8">
        <BreadCrumbs items={[{ key: "#", title: "Templates" }]} />
        <div className="mb-4">
          <Link to="/template-listing">
            <KeyboardBackspaceIcon /> Back
          </Link>
          <h2 className="heading uppercase">Add Email Templates</h2>
        </div>

        {/* Template Name Input */}
        <TextInput
          isError={errorInitiated && !formData.templateName.trim()}
          value={formData.templateName}
          name="templateName"
          onChangeFunc={handleChange}
          label="Template Name"
          errorMsg="Please provide a template name"
          isRequired
          placeholder="Name of template"
        />

        {/* Email Content Editor */}
        <div className="mt-4">
          <RichTextEditor
            value={formData.templateContent}
            onChange={newValue => handleChange(newValue)}
            errorInitiated={errorInitiated}
            errorMsg="Please provide template content"
          />
        </div>

        {/* Submit Button */}
        <div className="w-max">
          <Button className="w-[300px] mt-4" text="Submit" onClick={() => handleTemplateAction(tempPath ? "update" : "add")} />
        </div>
      </div>
    </main>
  );
};

export default AddEditTemplate;
